'use client'

import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { IChildrenProps } from '@/typings/children-props'

interface IIntegrationActivationContext {
    isActivated: (integrationId: string) => boolean
    activate: (integrationId: string) => void
}

const IntegrationActivationContext = createContext<IIntegrationActivationContext>({
    isActivated: () => false,
    activate: () => {},
})

export const IntegrationActivationProvider = ({ children }: IChildrenProps) => {
    const [activated, setActivated] = useState<ReadonlySet<string>>(() => new Set())

    const activate = useCallback((integrationId: string) => {
        setActivated((prev) => {
            if (prev.has(integrationId)) return prev

            const next = new Set(prev)
            next.add(integrationId)

            return next
        })
    }, [])

    const isActivated = useCallback(
        (integrationId: string) => activated.has(integrationId),
        [activated]
    )

    return (
        <IntegrationActivationContext.Provider
            value={useMemo(() => ({ isActivated, activate }), [isActivated, activate])}
        >
            {children}
        </IntegrationActivationContext.Provider>
    )
}

export const useIntegrationActivation = (integrationId: string) => {
    const { isActivated, activate } = useContext(IntegrationActivationContext)

    return useMemo(
        () => ({
            isActivated: isActivated(integrationId),
            activate: () => activate(integrationId),
        }),
        [isActivated, activate, integrationId]
    )
}
